import { useCallback, useEffect, useLayoutEffect } from 'react';

import { getCachedPermissions, initPermissions, useRbacStore } from './store';
import type { GetUserUis, RbacUser } from './types';

async function initPermissionsAndGetAppPermissions(
  user: RbacUser,
  getUserUisApi: GetUserUis,
  appType: ('console' | 'arc')[],
): Promise<string[]> {
  await initPermissions(user, getUserUisApi, appType);
  return useRbacStore.getState().appPermissions;
}

export interface UseRbacInitOptions {
  /** all permission checks will pass */
  whitelistMode?: boolean;
}

export interface UseRbacInitParams {
  user: RbacUser | null | undefined;
  getUserUisApi: GetUserUis;
  appType?: ('console' | 'arc')[];
  whitelistMode?: boolean;
}

export interface UseRbacInitReturn {
  initAndGetAppPermissions: (
    user: RbacUser,
    getUserUisApi: GetUserUis,
    appType?: ('console' | 'arc')[],
  ) => Promise<string[]>;
}

/**
 * Unified RBAC initialization: Continuously synchronize permissions in the App based on the current user.
 * Also returns initAndGetAppPermissions for scenarios like login where you need to initialize and fetch the app list once.
 * @param params.user - The current user (if undefined, only returns initAndGetAppPermissions, without running sync logic)
 * @param params.getUserUisApi - API to fetch the user's UI permissions
 * @param params.appType - Application type
 * @param params.whitelistMode - Whitelist mode
 * @returns initAndGetAppPermissions for awaiting after login and determining if a specific application is included
 */
export function useRbacInit(params: UseRbacInitParams): UseRbacInitReturn {
  const {
    user,
    getUserUisApi,
    appType,
    whitelistMode: whitelistModeOption,
  } = params;
  const whitelistMode = whitelistModeOption ?? false;

  useLayoutEffect(() => {
    useRbacStore.setState({ whitelistMode });
  }, [whitelistMode]);

  useLayoutEffect(() => {
    if (!user?.id) return;
    const cached = getCachedPermissions(user.id);
    if (cached && cached.uiPermissions.length >= 0) {
      useRbacStore.setState({
        uiPermissions: cached.uiPermissions,
        routePermissions: cached.routePermissions,
        appPermissions: cached.appPermissions ?? cached.uiPermissions,
        permissionsLoading: false,
      });
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.accessToken || !user?.id) return;
    const cached = getCachedPermissions(user.id);
    const hasNoCache = !cached || cached.uiPermissions.length === 0;
    if (hasNoCache) {
      useRbacStore.setState({ permissionsLoading: true });
    }
    initPermissions(user, getUserUisApi, appType).finally(() => {
      useRbacStore.setState({ permissionsLoading: false });
    });
  }, [user?.accessToken, user?.id, getUserUisApi, appType]);

  const initAndGetAppPermissions = useCallback(
    (
      u: RbacUser,
      getUisApi: GetUserUis,
      type: ('console' | 'arc')[] = ['console'],
    ) => initPermissionsAndGetAppPermissions(u, getUisApi, type),
    [],
  );

  return { initAndGetAppPermissions };
}
